import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

const FAQPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      question: "What types of real estate projects must be registered under RERA?",
      answer: "Any residential or commercial project exceeding 500 sq. mtrs. of land area or more than 8 units must be registered before advertising or selling.",
    },
    {
      question: "Can a project be marketed before obtaining RERA registration?",
      answer: "No, the promoter cannot advertise, market, or sell units without obtaining RERA registration.",
    },
    {
      question: "What documents are required for RERA project registration?",
      answer: "Key documents include:\n• Approved plans & layout\n• Title & land ownership documents\n• Commencement Certificate (CC)\n• PAN card and promoter details\n• Project specifications and declarations",
      isList: true,
    },
    {
      question: "What is the validity period of RERA registration?",
      answer: "The registration is valid up to the declared project completion date submitted at the time of registration.",
    },
    {
      question: "What is Quarterly Progress Report (QPR) in RERA?",
      answer: "It is a mandatory update filed every quarter by the promoter, showing construction progress, approvals, and sales status.",
    },
    {
      question: "Can a project be registered in phases under RERA?",
      answer: "Yes, large projects can be registered in multiple phases, and each phase is treated as a separate project.",
    },
    {
      question: "Is GST applicable on under-construction properties under RERA?",
      answer: "Yes, GST is applicable on under-construction properties, but not on completed properties where Occupancy Certificate (OC) is received.",
    },
    {
      question: "What happens if incorrect information is submitted during registration?",
      answer: "Providing false or misleading information may lead to penalties, rejection, or cancellation of registration.",
    },
    {
      question: "Can a promoter transfer project rights to another developer after registration?",
      answer: "Yes, but it requires prior approval from MahaRERA and consent of two-thirds of allottees.",
    },
    {
      question: "What is the penalty for non-registration of a project under RERA?",
      answer: "Penalty can be up to 10% of the project cost, and continued default may lead to imprisonment.",
    },
    {
      question: "What is an Agreement for Sale under RERA?",
      answer: "It is a legally binding document between the promoter and buyer, defining terms like possession date, payment schedule, and obligations.",
    },
    {
      question: "Can a buyer cancel the booking under RERA?",
      answer: "Yes, a buyer can cancel as per agreement terms, but deduction may apply as specified.",
    },
    {
      question: "What is the role of a RERA consultant?",
      answer: "A RERA consultant assists with:\n• Project registration\n• Compliance & filings\n• Legal documentation\n• Handling queries and approvals",
      isList: true,
    },
    {
      question: "Is it mandatory to disclose project details on the RERA website?",
      answer: "Yes, promoters must disclose all project details including plans, approvals, and progress updates on the official portal.",
    },
    {
      question: "What is the defect liability period under RERA?",
      answer: "The promoter is liable to fix any structural defects within 5 years from possession without extra charges.",
    },
    {
      question: "Can a project registration be revoked by MahaRERA?",
      answer: "Yes, if the promoter violates provisions or fails to comply, registration can be revoked.",
    },
    {
      question: "What is the difference between Carpet Area and Built-up Area?",
      answer: "• Carpet Area: Net usable area inside walls\n• Built-up Area: Carpet area + wall thickness",
      isList: true,
    },
    {
      question: "Can a buyer verify project approvals through RERA?",
      answer: "Yes, all approvals and project details are available on the MahaRERA portal for public access.",
    },
    {
      question: "Is RERA applicable to plotted development projects?",
      answer: "Yes, plotted developments also require registration if they meet the prescribed criteria.",
    },
    {
      question: "What support does your consultancy provide after registration?",
      answer: "We provide end-to-end support, including:\n• QPR filing\n• Form updates\n• Extension applications\n• Compliance management",
      isList: true,
    },
  ];

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const renderAnswer = (faq) => {
    if (faq.isList) {
      return (
        <div className="whitespace-pre-line">
          {faq.answer.split('\n').map((line, i) => (
            <div key={i} className={line.startsWith('•') ? 'ml-2' : ''}>{line}</div>
          ))}
        </div>
      );
    }
    return faq.answer;
  };

  return (
    <div className="min-h-screen fade-in" data-testid="faq-page">
      {/* Hero Section */}
      <section className="pt-32 pb-20 md:py-32 section-light" data-testid="faq-hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <h1
            className="text-5xl md:text-6xl tracking-tight leading-tight text-[#1A1A1A] mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="faq-hero-heading"
          >
            Frequently Asked Questions
          </h1>
          <p className="text-lg md:text-xl leading-relaxed text-[#555555] max-w-3xl">
            Find answers to common questions about RERA registration, compliance, and our services.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 md:py-32 section-light" data-testid="faq-content-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="faq-item bg-white rounded-lg border border-slate-200 overflow-hidden shadow-sm"
                data-testid={`faq-item-${index}`}
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-5 flex justify-between items-center text-left hover:bg-slate-50 transition-colors duration-200"
                  data-testid={`faq-question-${index}`}
                >
                  <span className="font-semibold text-[#1A1A1A] pr-4">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-[#C9A14A] flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-[#555555] flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-5 text-[#555555] leading-relaxed" data-testid={`faq-answer-${index}`}>
                    {renderAnswer(faq)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 section-grey border-t-4 border-[#C9A14A]" data-testid="faq-cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 text-center">
          <div className="bg-white p-12 rounded-lg shadow-sm border border-slate-100">
            <h2
              className="text-3xl md:text-5xl tracking-tight leading-snug text-[#1A1A1A] mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Still Have Questions?
            </h2>
            <p className="text-lg leading-relaxed text-[#555555] mb-8 max-w-2xl mx-auto">
              Our team is here to help. Reach out to us for personalized assistance with your RERA compliance needs.
            </p>
            <a
              href="/contact"
              className="inline-block btn-accent px-8 py-4 rounded-md font-medium text-lg"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
